import type {
  Quiz,
  QuizAnswer,
  QuizQuestion,
  QuizResult,
} from '@bulknutrients/bulk-shared-lib/interfaces/Quiz';
import {
  getAllQuizSlugs,
  getQuestionByRef,
  getQuizBySlug,
  getResultByRef,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/quiz';

import { client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { StandardLayout } from '@components/Layouts';
import { ProductCard } from '@components/ProductCard/ProductCard';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { EveryBodyTumbleWord } from '@components/WordTumbler/WordTumbler';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, useEffect, useState } from 'react';

interface QuizPageProps {
  quiz: Quiz;
}

function QuestionView({
  currentQuestion,
  handleAnswerClick,
  loading,
  history,
  handleBack,
}: {
  currentQuestion: QuizQuestion;
  handleAnswerClick: (answer: QuizAnswer) => void;
  loading: boolean;
  history: QuizQuestion[];
  handleBack: () => void;
}) {
  return (
    <div>
      <h2 className="uppercase m-0 mb-6">{currentQuestion.questionText}</h2>

      {currentQuestion.helpText && (
        <p className="mb-6 text-grey-dark dark:text-grey-light">
          {currentQuestion.helpText}
        </p>
      )}

      <div className="space-y-4">
        {currentQuestion.answers.map((answer) => (
          <Button
            key={answer._key}
            onClick={() => handleAnswerClick(answer)}
            buttonStyle="secondary"
            className="w-full h-fit"
            disabled={loading}
          >
            {answer.answerText}
          </Button>
        ))}
      </div>

      {history.length > 0 && (
        <div className="mt-6">
          <Button
            onClick={handleBack}
            buttonStyle="text"
            className="text-base"
            disabled={loading}
            icon={<Icons.Chevron.Left />}
          >
            Back
          </Button>
        </div>
      )}
    </div>
  );
}

function ResultView({
  result,
  recommendedSkus,
  handleBack,
  handleRestart,
}: {
  result: QuizResult;
  recommendedSkus: string[];
  handleBack: () => void;
  handleRestart: () => void;
}) {
  return (
    <div>
      <h2 className="uppercase m-0 mb-6">{result.title}</h2>

      {result.description && (
        <Paragraph className="mb-6" align="left">
          {result.description}
        </Paragraph>
      )}

      {result.recommendedProducts && result.recommendedProducts.length > 0 && (
        <>
          <div className="mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 *:last:col-span-full *:last:place-self-center">
              {result.recommendedProducts.map((product) => (
                <ProductCard
                  key={product.entityId}
                  product={product}
                  location="quiz-result"
                />
              ))}
            </div>
            <Paragraph className="my-4" align="left">
              {result.additionalContent}
            </Paragraph>
          </div>
          {recommendedSkus.length > 0 &&
            result.recommendedProductIds?.length && (
              <>
                <Button
                  buttonStyle="primary"
                  className="w-fit mx-auto"
                  href={`/api/promos/free-shaker?skus=${recommendedSkus.join(',')}&product_ids=${result.recommendedProductIds.join(',')}`}
                >
                  Add all to cart
                </Button>
                <Paragraph align="center" className="text-sm mb-4 ">
                  ...and we'll include a free shaker with your order!
                </Paragraph>
              </>
            )}
        </>
      )}

      <HorizontalDivider className="my-4" />
      <div className="flex gap-4">
        <Button
          onClick={handleBack}
          buttonStyle="tertiary"
          icon={<Icons.Chevron.Left />}
        >
          Back
        </Button>
        <Button onClick={handleRestart} buttonStyle="primary">
          Start Over
        </Button>
      </div>
    </div>
  );
}

export default function QuizPage({ quiz }: QuizPageProps): ReactElement {
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion | null>(
    null
  );
  const [history, setHistory] = useState<QuizQuestion[]>([]);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [recommendedSkus, setRecommendedSkus] = useState<string[]>([]);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Ensure hydration completes before rendering
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleAnswerClick = async (answer: QuizAnswer) => {
    // Add current question to history before moving forward
    if (currentQuestion) {
      setHistory([...history, currentQuestion]);
    }

    setLoading(true);

    try {
      if (answer.nextStep.resultRef) {
        // Fetch and show result
        const fetchedResult: QuizResult & { recommendedProductIds?: number[] } =
          await client.fetch(getResultByRef, {
            ref: answer.nextStep.resultRef,
          });

        // Fetch full product data with BigCommerce pricing
        if (fetchedResult.recommendedProductIds?.length) {
          const products = await fetch(
            `/api/product?product_ids=${fetchedResult.recommendedProductIds.join(',')}`,
            {
              method: 'GET',
            }
          ).then((res) => res.json().then((data) => data.data as any[]));
          fetchedResult.recommendedProducts = products;
        }

        setResult(fetchedResult);

        const skus =
          fetchedResult.recommendedProducts
            ?.map(
              (p) =>
                p.variants.find((v) => v.variantId === p.defaultVariantId)?.sku
            )
            .filter((sku: string | undefined) => sku !== undefined) || [];

        setRecommendedSkus(skus);
        setCurrentQuestion(null);
      } else if (answer.nextStep.questionRef) {
        // Fetch and move to next question
        const fetchedQuestion: QuizQuestion = await client.fetch(
          getQuestionByRef,
          {
            ref: answer.nextStep.questionRef,
          }
        );
        setCurrentQuestion(fetchedQuestion);
      }
    } catch {
      // Restore state if fetch failed
      if (currentQuestion && history.length > 0) {
        const newHistory = [...history];
        newHistory.pop();
        setHistory(newHistory);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (result) {
      // Go back from result to last question
      setResult(null);
      const lastQuestion = history[history.length - 1];
      setCurrentQuestion(lastQuestion);
      return;
    }

    if (history.length > 0) {
      const newHistory = [...history];
      const previousQuestion = newHistory.pop();
      setHistory(newHistory);
      setCurrentQuestion(previousQuestion || null);
    }
  };

  const handleStart = () => {
    setStarted(true);
    setCurrentQuestion(quiz.startingQuestion);
    setHistory([]);
    setResult(null);
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentQuestion(null);
    setHistory([]);
    setResult(null);
  };

  return (
    <>
      <Head>
        <title>{quiz.metaTitle || quiz.title}</title>
        <meta name="title" content={quiz.metaTitle || quiz.title} />
        <meta
          name="description"
          content={quiz.metaDescription || quiz.description || ''}
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/quiz/${quiz.slug.current}`}
        />
      </Head>

      <Container as="main" id="main-content" className="mb-12">
        <div className="mx-auto max-w-3xl justify-self-center">
          <h1 className="my-8 text-center not-italic">
            <EveryBodyTumbleWord />
          </h1>

          {quiz.description && !started && isHydrated && (
            <p className="mb-8 text-center text-lg">{quiz.description}</p>
          )}

          {/* Start View */}
          <div className="flex place-content-center min-h-[450px]">
            {!started && !result ? (
              <div>
                <Button onClick={handleStart} buttonStyle="primary">
                  Start Quiz
                </Button>
              </div>
            ) : null}

            {/* Question View */}
            {currentQuestion && (
              <QuestionView
                currentQuestion={currentQuestion}
                handleAnswerClick={handleAnswerClick}
                loading={loading}
                history={history}
                handleBack={handleBack}
              />
            )}

            {/* Result View */}
            {result && (
              <ResultView
                result={result}
                recommendedSkus={recommendedSkus}
                handleBack={handleBack}
                handleRestart={handleRestart}
              />
            )}
          </div>
        </div>
      </Container>
    </>
  );
}

QuizPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await client.fetch(getAllQuizSlugs);
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const quiz: Quiz = await client.fetch(getQuizBySlug, {
    slug: params?.quiz,
  });

  if (!quiz) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      quiz,
    },
  };
};
